<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useModalStore } from '@/store/modal.store';
import { useTeamStore } from '@/store/team.store';
import { useAuthStore } from "@/store/auth.store";
import { useRoute } from "vue-router";
import { IUser } from '@/types';
import DefaultBtn from "@/components/block/DefaultBtn.vue";

const loading = ref<boolean>(false);
const errorCMC = ref('');
const password = ref('');
const loadingCode = ref(false);
const password1 = ref<string>("");
const password2 = ref<string>("");
const code = ref<string>("");
const passwordError = ref<string>("");
const passwordMismatch = ref<boolean>(false);
const error = ref<string>("");
const upPassword = ref<boolean>(false);

const props = defineProps<{
	closeSignal?: boolean
}>();



watch(() => props.closeSignal, () => {
	if (props.closeSignal) {
		close();
	}
});

interface FirebaseError extends Error {
	code: string;
}

const visiblePassword = ref<boolean>(false);
const togglePasswordVisibility = () => {
	visiblePassword.value = !visiblePassword.value;
};

const modalStore = useModalStore();
const teamStore = useTeamStore();

const authStore = useAuthStore();
const route = useRoute();

const userId = computed<string>(() => String(modalStore.modalPayload.id || route.params.userId || authStore.user.id || '-'));
const originUser = computed<IUser | undefined>(() => teamStore.usersMap.get(userId.value));
const userData = ref<IUser | undefined>(originUser.value && JSON.parse(JSON.stringify(originUser.value)) || undefined);

const validatePassword = (password: string): boolean => {
	const minLength = 10;

	const hasNumber = /\d/.test(password);
	const hasUppercase = /[A-Z]/.test(password);
	const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

	if (password.length < minLength || !hasNumber || !hasUppercase || !hasSpecial) {
		passwordError.value = "Password does not meet the requirements above";
		return false;
	}

	passwordError.value = "";
	return true;
};

async function handleSubmit() {
	loading.value = true;
	try {
		if (password1.value == password2.value) {

			if (authStore.user.id === userId.value) {

				if (!validatePassword(password1.value)) {
					passwordError.value = "Passwords must match and meet the requirements.";
					loading.value = false;
					return;
				}

				teamStore.sendEditPasswordCode(userId.value)
					.then(() => {
						modalStore.openModal('ResetPasswordCode');
					});

			} else {
				teamStore.resetUserPassword(userId.value, password1.value)
					.then(() => {
						modalStore.openModal('CodePasswordUser');
					});
			}
		}
	} catch (error) {
		console.error('Error updating user data:', error);
	}
}

async function handleSubmitWithCode() {
	try {
		loadingCode.value = true;
		if (userData.value) {

			if (authStore.user.id === userId.value)

				Promise.all([
					teamStore.resetUserPassword(userId.value, password1.value, code.value),
				])
					.then(() => {
						modalStore.openModal('CodePasswordUser');
					});
		}
	} catch (error) {
		console.error('Error updating user data:', error);
		loadingCode.value = false;
	}
}

const buttonText = ref('Send code again');
const sendCode = () => {
	buttonText.value = 'New code was sent!';
	handleSubmit();
};

const copyPassword = () => {
	if (password1.value) {
		navigator.clipboard.writeText(password1.value).then(() => {
		}).catch(err => {
			console.log('Failed to copy password: ' + err);
		});
	} else {
		console.log('No password to copy');
	}
};


</script>

<template>
	<div   v-if="modalStore.isModalVisible('ResetPasswordUser') " class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">lock_reset</span>
					<span>Password Reset</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main modal__hero" v-if="!upPassword">
				<p class="modal__main_title">Enter new password.</p>
				<p class="modal__main_text">Make sure to follow these requirements <br> for your new password</p>
				<ul class="modal__main_list">
					<li><b>min 10 characters</b></li>
					<li><b>at least one symbol</b></li>
					<li><b>at least one capital letter</b></li>
					<li><b>at least one number</b></li>
				</ul>
				<br>
				<div class="login__item">
					<input
						v-model="password1"
						:type="visiblePassword ? 'text' : 'password'"
						class="login__password"
						placeholder="Password">

					<span class="material-symbols-outlined login__item_ico">lock</span>
					<button class="login__item_btn" @click="togglePasswordVisibility">
						<span class="material-symbols-outlined login__item_eyes eyes--off" :class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
						<span class="material-symbols-outlined login__item_eyes eyes--visible" :class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
					</button>

					<p class="error" v-if="passwordError">{{ passwordError }}</p>
					<p class="error" v-if="error">{{ error }}</p>
				</div>

				<div class="login__item">
					<input
						v-model="password2"
						:type="visiblePassword ? 'text' : 'password'"
						class="login__password"
						placeholder="Confirm Password">

					<span class="material-symbols-outlined login__item_ico">lock</span>
					<button class="login__item_btn" @click="togglePasswordVisibility">
						<span class="material-symbols-outlined login__item_eyes eyes--off" :class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
						<span class="material-symbols-outlined login__item_eyes eyes--visible" :class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
					</button>

					<p class="error" v-if="passwordMismatch">Passwords do not match.</p>
				</div>

	
				<DefaultBtn type="submit" class="login__btn btn__code" :loading="loading" text="Continue" @click="handleSubmit"/>
			</div>
			<div class="modal__main" v-else>
				<div class="modal__main_img"><img src="/img/check_circle.png" alt=""></div>
				<p class="modal__main_title">Password updated!</p>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('ResetPasswordCode')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">lock</span>
					<span>Password Setup</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>

				<div class="modal__main">
					<form @submit.prevent="handleSubmitWithCode">
					<p class="modal__update">Enter security code.</p>
					<p class="modal__subtitle">Your password has been updated. Enter the code we<br> sent to your email
						address.</p>
					<div class="modal__main_wrap">
						<div class="login__item">
							<input autocomplete="one-time-code" v-model="code" :type="visiblePassword ? 'number' : 'password'"
								class="login__password" placeholder="Enter Code">

							<span class="material-symbols-outlined login__item_ico ico__code">pin</span>
							<button class="login__item_btn" type="button" @click="togglePasswordVisibility">
								<span class="material-symbols-outlined login__item_eyes eyes--off"
									:class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
								<span class="material-symbols-outlined login__item_eyes eyes--visible"
									:class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
							</button>

							<p class="error" v-if="errorCMC">{{ errorCMC }}</p>
						</div>
					</div>
					<DefaultBtn class="btn btn__code" type="submit" :loading="loadingCode" text="Continue" />
					<button to="/"  class="login__reset"  @click.prevent="sendCode">{{ buttonText }}</button>
				</form>
				</div>
	
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('CodePasswordUser')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">lock_reset</span>
					<span>Password Reset</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<div class="modal__main_img"><img src="/img/check_circle.png" alt=""></div>
				<p class="modal__main_title">Password was successfully changed. </p>

				<div class="login__item">
					<input
						v-model="password1"
						:type="visiblePassword ? 'text' : 'password'"
						id="repeatPassword"
						class="login__password"
						placeholder="Copy Password"
						readonly>

					<span class="material-symbols-outlined login__item_ico">lock</span>
					<button class="login__item_btn"@click="togglePasswordVisibility">
						<span
							class="material-symbols-outlined login__item_eyes eyes--off"
							:class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
						<span
							class="material-symbols-outlined login__item_eyes eyes--visible"
							:class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
					</button>
				</div>

				<button @click="copyPassword" type="button" class="login__btn">
					<span class="material-symbols-outlined">content_copy</span>
					Copy Password
				</button>

			</div>
		</div>
	</div>
</template>


<style scoped>

	.modal__subtitle{
		text-align: center;
		font-size: 16px;
		line-height: 25px;
		margin-bottom: 32px;
	}

	.login__reset{
		background: none;
		border: none;
		font-size: 16px;
		margin-top: 30px;
	}
	.btn{
		box-shadow: none;
	}

	form{
		display: flex;
		align-items: center;
		flex-direction: column;
	}
	.modal__hero{
		padding-bottom: 100px;
	}

</style>